import * as CL from '@design-system/component-library';
import { useRevalidator } from 'react-router';
import { cancelInvitation, resendInvitation } from '../../common/fetch';
import {
  cancelMsg,
  invitationRemovalFailedMsg,
  invitationRemovedSuccessfullyMsg,
  invitationSendingFailedMsg,
  invitationSentSuccessfullyMsg,
  removeMsg,
  t,
} from '../../common/i18n';
import { addNotification } from '../../public/site/notificationStore.js';

import './InvitationActionButton.scss';

export enum InvitationActionType {
  CANCEL = 'cancel',
  REMOVE = 'remove',
  RESEND = 'resend',
}

export interface InvitationActionButtonProps {
  invitationId: string;
  actionType: InvitationActionType;
  buttonColor?: CL.ButtonProps['color'];
}

export const InvitationActionButton = ({ invitationId, actionType, buttonColor }: InvitationActionButtonProps) => {
  const { revalidate } = useRevalidator();

  const cancel = async () => {
    const response = await cancelInvitation(invitationId);
    if (response.ok) {
      revalidate();
      addNotification(t.PF1Y(invitationRemovedSuccessfullyMsg), 'success');
    } else {
      addNotification(t.VFVT(invitationRemovalFailedMsg), 'error');
    }
  };

  const resend = async () => {
    const response = await resendInvitation(invitationId);
    if (response.ok) {
      revalidate();
      addNotification(t.LSZS(invitationSentSuccessfullyMsg), 'success');
    } else {
      addNotification(t.AANC(invitationSendingFailedMsg), 'error');
    }
  };

  const getInvitationAction = () => {
    switch (actionType) {
      case InvitationActionType.CANCEL:
        return {
          icon: 'block',
          text: t.B2V1(cancelMsg),
          onClick: cancel,
        };
      case InvitationActionType.REMOVE:
        return {
          icon: 'delete',
          text: t.R3VE(removeMsg),
          onClick: cancel,
        };
      case InvitationActionType.RESEND:
        return {
          icon: 'send',
          text: t.HVWH('Resend'),
          onClick: resend,
        };
    }
  };

  const action = getInvitationAction();

  return (
    <CL.Button
      className="of-invitation-action-button"
      color={buttonColor ?? 'link'}
      icon={<CL.Icon icon={action.icon} />}
      onClick={action.onClick}
    >
      {action.text}
    </CL.Button>
  );
};

export const getInvitationActionButtons = (
  invitationId: string,
  actionTypes: InvitationActionType[],
  buttonColor?: CL.ButtonProps['color']
) => {
  return actionTypes.map(actionType => (
    <InvitationActionButton
      key={invitationId + actionType}
      invitationId={invitationId}
      actionType={actionType}
      buttonColor={buttonColor}
    />
  ));
};
